import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ApiCrudSection, {
  type Column,
  type FormField,
  type Row,
} from "../components/ApiCrudSection";
import {
  fetchContributions,
  createContribution,
  updateContribution,
  deleteContribution,
} from "../services/payrollService";
import {
  CONTRIBUTION_TYPE_OPTIONS,
  contributionTypeLabel,
  formatAmount,
} from "../theme";
import type { ContributionParam, ContributionType } from "../types";
import { useIsStaffAdmin } from "../../../hooks/useIsStaffAdmin";

// ---------------------------------------------------------------------------
// Parámetros previsionales (AgroCloud#38): las entidades y porcentajes que se descuentan
// del sueldo (pensión, salud, cesantía, impuestos) y lo que aporta el empleador.
//
// El % del colaborador se le descuenta del líquido; el del EMPLEADOR no toca el
// líquido y se informa aparte como costo empresa — confundirlos le quitaría al
// colaborador dinero que no le corresponde pagar.
//
// El TOPE IMPONIBLE acota la base: sobre lo que excede el tope no se descuenta.
//
// ⚠️ DEUDA CONSCIENTE (documentada en el PR): los porcentajes y topes los carga
// el usuario. Este módulo NO trae precargado el cuadro previsional de ningún
// país ni lo actualiza solo: hacerlo sin la validación de un contador local
// sería inventar la ley de un país.
// ---------------------------------------------------------------------------

/** Número del formulario, o `null` si no se informó (≠ 0). */
function numOrNull(raw: string | undefined): number | null {
  if (raw === undefined || raw === null || String(raw).trim() === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function toPayload(values: Record<string, string>): Partial<ContributionParam> {
  return {
    name: values.name,
    type: (values.type || "other") as ContributionType,
    employeePercentage: numOrNull(values.employeePercentage),
    employerPercentage: numOrNull(values.employerPercentage),
    taxableCap: numOrNull(values.taxableCap),
    active: values.active !== "false",
    notes: values.notes || null,
  };
}

export default function ContributionSection() {
  // #1007: solo quien administra modifica; los demás ven lo suyo en solo lectura.
  const isStaffAdmin = useIsStaffAdmin("payroll");
  const { t } = useTranslation("common");
  const [rows, setRows] = useState<ContributionParam[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items } = await fetchContributions();
      setRows(items);
    } catch {
      setError(t("payroll.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const pct = (v: number | null | undefined) =>
    v === null || v === undefined ? "—" : `${v} %`;

  const columns: Column[] = [
    { key: "name", label: t("payroll.fields.name") },
    {
      key: "type",
      label: t("payroll.fields.contributionType"),
      render: (row) => {
        const type = String(row.type || "other");
        return contributionTypeLabel(t, type);
      },
    },
    {
      key: "employeePercentage",
      label: t("payroll.fields.employeePercentage"),
      align: "right",
      render: (row) => pct(row.employeePercentage as number | null),
    },
    {
      key: "employerPercentage",
      label: t("payroll.fields.employerPercentage"),
      align: "right",
      render: (row) => pct(row.employerPercentage as number | null),
    },
    {
      key: "taxableCap",
      label: t("payroll.fields.taxableCap"),
      align: "right",
      // "—" = sin tope, que NO es lo mismo que un tope de 0.
      render: (row) =>
        formatAmount(row.taxableCap as number | null, String(row.currency || "MXN")),
    },
    {
      key: "active",
      label: t("payroll.fields.active"),
      render: (row) =>
        row.active === false ? (
          <span className="rounded-md bg-gray-400/15 px-2 py-0.5 text-[11px] font-medium text-gray-500">
            {t("payroll.inactive")}
          </span>
        ) : (
          <span className="rounded-md bg-emerald-500/15 px-2 py-0.5 text-[11px] font-medium text-emerald-500">
            {t("payroll.active")}
          </span>
        ),
    },
  ];

  const fields: FormField[] = [
    {
      key: "name",
      label: t("payroll.fields.name"),
      required: true,
      placeholder: "IMSS / Afore / ISR",
    },
    {
      key: "type",
      label: t("payroll.fields.contributionType"),
      type: "select",
      defaultValue: "pension",
      options: CONTRIBUTION_TYPE_OPTIONS.map((v) => ({
        value: v,
        label: contributionTypeLabel(t, v),
      })),
    },
    {
      key: "employeePercentage",
      label: t("payroll.fields.employeePercentage"),
      type: "number",
      hint: t("payroll.hints.employeePercentage"),
    },
    {
      key: "employerPercentage",
      label: t("payroll.fields.employerPercentage"),
      type: "number",
      hint: t("payroll.hints.employerPercentage"),
    },
    {
      key: "taxableCap",
      label: t("payroll.fields.taxableCap"),
      type: "number",
      hint: t("payroll.hints.taxableCap"),
    },
    { key: "active", label: t("payroll.fields.active"), type: "checkbox", defaultValue: true },
    { key: "notes", label: t("payroll.fields.notes"), type: "textarea" },
  ];

  const banner = (
    <div className="mb-4 rounded-lg border border-sky-500/30 bg-sky-500/10 px-4 py-3 text-sm text-sky-500">
      <i className="ri-information-line mr-1" />
      {t("payroll.contributionsNotice")}
    </div>
  );

  const handleCreate = async (values: Record<string, string>) => {
    await createContribution(toPayload(values));
    await load();
  };

  const handleUpdate = async (id: string, values: Record<string, string>) => {
    await updateContribution(id, toPayload(values));
    await load();
  };

  const handleDelete = async (id: string) => {
    await deleteContribution(id);
    await load();
  };

  return (
    <ApiCrudSection
      readOnly={!isStaffAdmin}
      title={t("payroll.menu.contribution")}
      subtitle={t("payroll.contributionSubtitle")}
      icon="ri-shield-user-line"
      addLabel={t("payroll.newContribution")}
      columns={columns}
      fields={fields}
      rows={rows as unknown as Row[]}
      loading={loading}
      error={error}
      searchKey="name"
      banner={banner}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
    />
  );
}
